import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Question Type Definitions
interface BaseQuestion {
    type: string;
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    correct: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[]; // Fill-in options for user to choose
    correct: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    correct: boolean;
}

interface MatchThePairQuestion extends BaseQuestion {
    type: "match";
    options: string[];
    labeled: { "A": string[], "B": string[] };
    correct: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion
    | MatchThePairQuestion;

// Define types
type SyllabusType =
    | "All Chapters"


interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
    syllabus?: SyllabusType;
}



const syllabusFiles: Record<SyllabusType, string> = {
    "All Chapters": "TransportsforLKG.json"
}

const Transports: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        const topics = Object.keys(syllabusFiles) as SyllabusType[];
        const randomTopic = topics[Math.floor(Math.random() * topics.length)];
        const filename = syllabusFiles[randomTopic];

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "/" + filename
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/${filename}`
                );
                data = module.default;
            }
            setQuestions(shuffleArray(data));
            const choosedOne = data[Math.floor(Math.random() * data.length)];
            setCurrentQuestion(choosedOne);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.correct;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false" || currentQuestion.type === "match") {
            correctAnswer = currentQuestion.correct;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                loadRandomSyllabusQuestions();
                setSelectedOption(null);
            }, 4000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Modes of Transport Worksheet for LKG | Land, Air & Water Transport";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Introduce LKG kids to modes of transport with fun and engaging worksheets. Learn about land, air, and water transport with sorting, matching, and coloring activities." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Modes of Transport</h1>

                            <p>
                                Understanding how we travel from one place to another is an important part of a child’s early learning. These <strong>transport worksheets for LKG</strong> introduce children to <strong>land, air, and water transport</strong> through colorful illustrations, matching tasks, and simple identification activities.
                            </p>

                            <h2>🚗 Why Teach Modes of Transport in LKG?</h2>
                            <p>
                                Teaching about transport helps children:
                                <ul>
                                    <li>Understand how people and goods move</li>
                                    <li>Classify transport based on where it travels (<strong>land, air, or water</strong>)</li>
                                    <li>Recognize common vehicles and their uses</li>
                                    <li>Develop general awareness of the world around them</li>
                                </ul>
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify different vehicles <strong>like cars, buses, airplanes, trains, and boats</strong></li>
                                <li>Classify vehicles by <strong>mode: land, air, or water</strong></li>
                                <li>Match vehicles with places they belong (<strong>e.g. airplane → airport</strong>)</li>
                                <li>Enhance vocabulary and observation skills</li>
                            </ul>

                            <h2>📋 Activities Included in the Worksheet</h2>
                            <ul>
                                <li>Match the transport to the mode (<strong>e.g. Ship → Water</strong>)</li>
                                <li>Circle land vehicles from a group</li>
                                <li>Color the correct transport shown in images</li>
                                <li>Fill in the blanks: <strong>“A car runs on _____”</strong></li>
                                <li>Guess the transport from the given clues</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Use toy vehicles to explain transport modes</li>
                                <li>Read storybooks featuring different types of transport</li>
                                <li>Visit a railway station or airport (if possible) for real-life observation</li>
                                <li>Play sorting games with <strong>land, air, and water</strong> labels</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/general-awareness/people-who-help-us"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>People Who Helps Us</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/seasons"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Seasons</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/general-awareness/good-habits"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Good Habits & Manners</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Make learning fun with songs, storytelling, and movement-based games. The more they move and point, the better they learn!
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Modes of Transport</h1>

                        <p>
                            Understanding how we travel from one place to another is an important part of a child’s early learning. These <strong>transport worksheets for LKG</strong> introduce children to <strong>land, air, and water transport</strong> through colorful illustrations, matching tasks, and simple identification activities.
                        </p>

                        <h2>🚗 Why Teach Modes of Transport in LKG?</h2>
                        <p>
                            Teaching about transport helps children:
                            <ul>
                                <li>Understand how people and goods move</li>
                                <li>Classify transport based on where it travels (<strong>land, air, or water</strong>)</li>
                                <li>Recognize common vehicles and their uses</li>
                                <li>Develop general awareness of the world around them</li>
                            </ul>
                        </p>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Identify different vehicles <strong>like cars, buses, airplanes, trains, and boats</strong></li>
                            <li>Classify vehicles by <strong>mode: land, air, or water</strong></li>
                            <li>Match vehicles with places they belong (<strong>e.g. airplane → airport</strong>)</li>
                            <li>Enhance vocabulary and observation skills</li>
                        </ul>

                        <h2>📋 Activities Included in the Worksheet</h2>
                        <ul>
                            <li>Match the transport to the mode (<strong>e.g. Ship → Water</strong>)</li>
                            <li>Circle land vehicles from a group</li>
                            <li>Color the correct transport shown in images</li>
                            <li>Fill in the blanks: <strong>“A car runs on _____”</strong></li>
                            <li>Guess the transport from the given clues</li>
                        </ul>

                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>Use toy vehicles to explain transport modes</li>
                            <li>Read storybooks featuring different types of transport</li>
                            <li>Visit a railway station or airport (if possible) for real-life observation</li>
                            <li>Play sorting games with <strong>land, air, and water</strong> labels</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/lkg/general-awareness/people-who-help-us"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>People Who Helps Us</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/seasons"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Seasons</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/lkg/general-awareness/good-habits"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Good Habits & Manners</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🌟 Mama’s Guidance Tip</h2>
                        <p>
                            Make learning fun with songs, storytelling, and movement-based games. The more they move and point, the better they learn!
                        </p>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.correct).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                            {currentQuestion.type === "match" && (
                                <>
                                    <table className="table-bordered table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Column A</th>
                                                <th scope="col">Column B</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentQuestion.labeled.A.map((leftItem, index) => (
                                                <tr key={index}>
                                                    <td>{leftItem}</td>
                                                    <td>{currentQuestion.labeled.B[index]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.correct.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                </>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default Transports;
